/**
 * jctc/event-calendar block interaction.
 *
 * Month paging: an AJAX round-trip (get_calendar_month, inc/ajax-calendar.php)
 * returns the same template-parts/event-calendar-grid.php markup the block's
 * own initial render used, and this module swaps .jctc-event-calendar__inner
 * wholesale -- server stays the single source of truth for the grid, no
 * client-side date math duplicated here.
 *
 * Each day cell's events render directly, always -- there's no expand/collapse
 * step here to rebind. Clicking an individual event (.js-event-card) opens the
 * shared event-detail modal via assets/src/js/modules/events.js' own global
 * click handler, which needs no calendar-specific wiring.
 *
 * The compact <desktop agenda (template-parts/event-calendar-grid.php) opens
 * a day's event panel via a real <a href="#jctc-cal-N-day-D">, pure CSS
 * :target -- no JS needed for the open/scroll-into-view behavior itself.
 * But :target reacts to whatever fragment is already in the URL when the
 * page loads, not just a click made during THIS visit -- a reload, a
 * revisited/bookmarked/shared link, or history back/forward can all land on
 * that same URL with the hash still attached, which would silently reopen a
 * panel the user never actually clicked this time. clearStaleAgendaHash()
 * strips exactly that one hash shape before the calendar finishes
 * initializing, so every fresh page view starts with no panel open -- a
 * live click during the visit still works exactly as before, untouched.
 *
 * Two steps, not one -- confirmed live, both are required: setting
 * location.hash to '' (or history.replaceState alone) changes the address
 * bar but does NOT actually un-match :target in Chromium; the element stays
 * visibly "open" even though location.hash now reads empty. Re-pointing the
 * hash at a decoy id that matches no element (#jctc-cal-closed) is what
 * actually clears :target, and only THEN is history.replaceState safe to
 * tidy the visible URL back to plain, since :target is already cleared by
 * that point and replaceState alone can't re-trigger it.
 *
 * Month swap transition (JCTCTAS-40, revised after client feedback):
 * prev/next used to call oldInner.replaceWith(newInner) directly -- an
 * instant, un-animated swap of the ENTIRE `.jctc-event-calendar__inner`
 * subtree, header bar included. A first pass fixed the "instant" part by
 * fading that whole subtree out/in, but that made the gold header bar
 * (title, month/year label, prev/next buttons) itself flicker out and
 * back in on every click, and swapping the whole subtree wholesale read
 * as "part of the calendar got deleted and re-created" -- not what was
 * asked for. Per explicit correction: the header bar (and the static
 * Sun-Sat weekday-label row, which never changes between months at all)
 * must never fade or be removed/recreated -- only its TEXT/data updates,
 * instantly, in place. Fading is for the day-cell content only (the
 * desktop grid and the <desktop agenda variant), and even that must not
 * rebuild the DOM further than swapping those two specific elements --
 * the outer container, header bar, and weekday row are never touched.
 *
 * Label speed (JCTCTAS reported live, 2026-09-09): the month/year label used
 * to update only once updateCalendarMonth() ran after the AJAX response --
 * confirmed live (Playwright timing instrumentation) that response alone
 * takes ~500-800ms (re-rendering the full grid+agenda server-side), on top
 * of which the grid's own fade adds ~200ms more -- so the text looked like
 * part of a slow ~1s+ animation even though nothing about the label itself
 * was ever animated. The label's new value is fully knowable from the
 * clicked button's own data-year/data-month with no server round-trip at
 * all, so the click handler (see bind() below) now sets it immediately,
 * synchronously, before the fetch even starts. updateHeaderInPlace() below
 * still re-applies the server's own value once the response lands (belt and
 * suspenders against any client/server month-name mismatch), which by then
 * is a same-value no-op the vast majority of the time.
 *
 * updateCalendarMonth() below:
 *   1. Copies the new month's data-* attributes onto the EXISTING
 *      `.jctc-event-calendar__inner` (fetchMonth() reads these as "the
 *      currently-displayed month" on the next click).
 *   2. Updates the EXISTING header bar's month/year label text and the
 *      EXISTING prev/next buttons' data-year/data-month IN PLACE --
 *      these button nodes are never removed, so the single click
 *      listener bound once at init (see bind() below) keeps working
 *      correctly forever; nothing to ever re-bind.
 *   3. Fades ONLY `.jctc-event-calendar__grid` and `.jctc-event-calendar__agenda`
 *      out (`.js-fade-leaving`, assets/src/scss/base/_base.scss), swaps
 *      just those two elements once each transition actually finishes
 *      (or a safety timeout fires, in case `transitionend` never does),
 *      then fades each new one in via the same shared `.js-card-enter`
 *      entrance animation every AJAX load-more surface already uses
 *      (JCTCTAS-39) -- one consistent motion language sitewide, no
 *      second bespoke keyframes rule. Both skipped entirely under
 *      `prefers-reduced-motion`.
 */

// Must match _base.scss's own .js-fade-leaving calc(... * 0.45) exactly --
// the leaving transition is intentionally shorter than the configured
// speed (see that rule's own doc comment for why), and this timeout has to
// agree with the real CSS duration it's a fallback for.
const FADE_OUT_RATIO = 0.45;

// Reads the block instance's own --jctc-card-fade-duration (set inline on
// .jctc-event-calendar by blocks/event-calendar/render.php's "Month-Change
// Fade Speed" field) back out as a number, so the transitionend
// safety-timeout below can't fire before the real CSS transition it's
// timing has had a chance to finish. Falls back to 350 (matching that
// field's own default) if the property somehow isn't set at all -- e.g. a
// stale cached render from before this field existed.
const getCellFadeOutMs = (container) => {
    const raw = getComputedStyle(container).getPropertyValue('--jctc-card-fade-duration');
    const parsed = parseFloat(raw);
    const totalMs = Number.isFinite(parsed) && parsed > 0 ? parsed : 350;
    return totalMs * FADE_OUT_RATIO;
};

// Fades oldEl out, swaps it for newEl only once that transition actually
// finishes (or the safety timeout fires), then fades newEl in. Used for
// exactly the two day-cell containers (grid + agenda) -- never the header
// bar or weekday row, which update via updateHeaderInPlace() instead and
// are never removed from the DOM at all.
const swapCellsWithFade = (oldEl, newEl, cellFadeOutMs) => {
    if (!oldEl || !newEl) {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        oldEl.replaceWith(newEl);
        return;
    }

    let swapped = false;
    const doSwap = () => {
        if (swapped) return;
        swapped = true;
        oldEl.replaceWith(newEl);
        newEl.classList.remove('is-loading');
        newEl.classList.add('js-card-enter');
        newEl.addEventListener('animationend', () => newEl.classList.remove('js-card-enter'), {
            once: true,
        });
    };

    oldEl.classList.remove('is-loading');
    oldEl.classList.add('js-fade-leaving');
    oldEl.addEventListener('transitionend', doSwap, { once: true });
    // Safety fallback -- `transitionend` doesn't fire if the element is
    // removed from the render tree before the transition completes (e.g. a
    // fast double-click on prev/next), which would otherwise leave the new
    // month's cells never inserted.
    setTimeout(doSwap, cellFadeOutMs + 50);
};

// Updates the header bar's month/year label and the prev/next buttons'
// own data-year/data-month to match the newly-fetched month -- never
// removes or replaces these nodes, so their one click listener (bound
// once, at init) keeps reading correct values on every future click with
// nothing to ever re-bind.
const updateHeaderInPlace = (oldInner, newInner) => {
    const oldLabel = oldInner.querySelector('.jctc-header-bar__nav-label');
    const newLabel = newInner.querySelector('.jctc-header-bar__nav-label');
    if (oldLabel && newLabel) {
        oldLabel.textContent = newLabel.textContent;
    }

    ['prev', 'next'].forEach((direction) => {
        const oldBtn = oldInner.querySelector(`.jctc-header-bar__nav-btn--${direction}`);
        const newBtn = newInner.querySelector(`.jctc-header-bar__nav-btn--${direction}`);
        if (oldBtn && newBtn) {
            oldBtn.dataset.year = newBtn.dataset.year;
            oldBtn.dataset.month = newBtn.dataset.month;
        }
    });
};

const updateCalendarMonth = (oldInner, newInner, cellFadeOutMs) => {
    Object.keys(newInner.dataset).forEach((key) => {
        oldInner.dataset[key] = newInner.dataset[key];
    });

    updateHeaderInPlace(oldInner, newInner);

    swapCellsWithFade(
        oldInner.querySelector('.jctc-event-calendar__grid'),
        newInner.querySelector('.jctc-event-calendar__grid'),
        cellFadeOutMs
    );
    swapCellsWithFade(
        oldInner.querySelector('.jctc-event-calendar__agenda'),
        newInner.querySelector('.jctc-event-calendar__agenda'),
        cellFadeOutMs
    );
};

const clearStaleAgendaHash = () => {
    if (/^#jctc-cal-[\w-]+-day-\d+$/.test(window.location.hash)) {
        window.location.hash = '#jctc-cal-closed';
        history.replaceState(null, '', window.location.pathname + window.location.search);
    }
};

const getAjaxUrl = () => window.jctc_vars?.ajax_url || '/wp-admin/admin-ajax.php';
const getNonce = () => window.jctc_vars?.nonce || '';

// Matches the server's own date_i18n('F Y', ..., true) formatting
// (template-parts/event-calendar-grid.php) -- UTC so the browser's own
// local timezone can't shift the displayed month, exactly the class of bug
// that same server-side line was just fixed for (see the module doc
// comment above). document.documentElement.lang (WordPress's own
// language_attributes() output) keeps this in step with the site's actual
// language if it's ever changed from en-US.
const formatMonthLabel = (year, month) => {
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
};

// In-Memory cache for rendered month HTML (JCTCTAS-547)
const monthCache = new Map();

const getCacheKey = (title, year, month, showTestEvents, testEventCount, showPastEvents) =>
    `${title}|${year}|${month}|${showTestEvents ? '1' : '0'}|${testEventCount}|${showPastEvents ? '1' : '0'}`;

const initCalendarInstance = (container) => {
    const getParams = () => {
        const currentInner = container.querySelector('.jctc-event-calendar__inner');
        return {
            title: currentInner?.dataset.title || '',
            showTestEvents: currentInner?.dataset.showTestEvents === '1',
            testEventCount: currentInner?.dataset.testEventCount || '4',
            showPastEvents: currentInner?.dataset.showPastEvents === '1',
        };
    };

    // Prefetches adjacent months during idle periods (JCTCTAS-547)
    const prefetchMonth = async (year, month) => {
        const { title, showTestEvents, testEventCount, showPastEvents } = getParams();
        const key = getCacheKey(title, year, month, showTestEvents, testEventCount, showPastEvents);
        if (monthCache.has(key)) return;

        try {
            const formData = new FormData();
            formData.append('action', 'get_calendar_month');
            formData.append('title', title);
            formData.append('year', String(year));
            formData.append('month', String(month));
            formData.append('show_test_events', showTestEvents ? '1' : '');
            formData.append('test_event_count', testEventCount);
            formData.append('show_past_events', showPastEvents ? '1' : '');
            formData.append('nonce', getNonce());

            const response = await fetch(getAjaxUrl(), {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();
            if (result.success && result.data?.html) {
                monthCache.set(key, result.data.html.trim());
            }
        } catch {
            // Background prefetch failure is non-blocking
        }
    };

    const schedulePrefetch = (year, month) => {
        const y = Number(year);
        const m = Number(month);
        const prevM = m === 1 ? 12 : m - 1;
        const prevY = m === 1 ? y - 1 : y;
        const nextM = m === 12 ? 1 : m + 1;
        const nextY = m === 12 ? y + 1 : y;

        const run = () => {
            prefetchMonth(prevY, prevM);
            prefetchMonth(nextY, nextM);
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(run, { timeout: 2000 });
        } else {
            setTimeout(run, 400);
        }
    };

    const applyMonthHtml = (html) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const newInner = wrapper.firstElementChild;
        const oldInner = container.querySelector('.jctc-event-calendar__inner');

        if (oldInner && newInner) {
            updateCalendarMonth(oldInner, newInner, getCellFadeOutMs(container));
        }
    };

    const fetchMonth = async (year, month) => {
        const oldInner = container.querySelector('.jctc-event-calendar__inner');
        const grid = oldInner?.querySelector('.jctc-event-calendar__grid');
        const agenda = oldInner?.querySelector('.jctc-event-calendar__agenda');

        // Immediate visual feedback (opacity: 0.4 within 80ms)
        grid?.classList.add('is-loading');
        agenda?.classList.add('is-loading');

        const { title, showTestEvents, testEventCount, showPastEvents } = getParams();
        const key = getCacheKey(title, year, month, showTestEvents, testEventCount, showPastEvents);

        // Instant cache hit: 0ms network round-trip
        if (monthCache.has(key)) {
            applyMonthHtml(monthCache.get(key));
            schedulePrefetch(year, month);
            return;
        }

        const formData = new FormData();
        formData.append('action', 'get_calendar_month');
        formData.append('title', title);
        formData.append('year', String(year));
        formData.append('month', String(month));
        formData.append('show_test_events', showTestEvents ? '1' : '');
        formData.append('test_event_count', testEventCount);
        formData.append('show_past_events', showPastEvents ? '1' : '');
        formData.append('nonce', getNonce());

        try {
            const response = await fetch(getAjaxUrl(), {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();

            if (!result.success || !result.data?.html) {
                throw new Error(result.data?.message || 'Calendar month load failed.');
            }

            const html = result.data.html.trim();
            monthCache.set(key, html);
            applyMonthHtml(html);
            schedulePrefetch(year, month);
        } catch (error) {
            grid?.classList.remove('is-loading');
            agenda?.classList.remove('is-loading');
            console.error('Calendar month load failed:', error);
        }
    };

    // Cache initial month and prefetch adjacent months
    const initialInner = container.querySelector('.jctc-event-calendar__inner');
    if (initialInner?.dataset?.year && initialInner?.dataset?.month) {
        const { title, showTestEvents, testEventCount, showPastEvents } = getParams();
        const initialKey = getCacheKey(
            title,
            initialInner.dataset.year,
            initialInner.dataset.month,
            showTestEvents,
            testEventCount,
            showPastEvents
        );
        monthCache.set(initialKey, initialInner.outerHTML);
        schedulePrefetch(initialInner.dataset.year, initialInner.dataset.month);
    }

    // Runs exactly once, at init -- the header bar's own prev/next buttons
    // are never removed or replaced by updateCalendarMonth() above (only
    // their data-year/data-month change, in place), so the single click
    // listener attached here keeps reading correct values on every future
    // month change with nothing left to ever re-bind.
    function bind() {
        const inner = container.querySelector('.jctc-event-calendar__inner');
        if (!inner) {
            return;
        }

        inner.querySelectorAll('.js-calendar-nav').forEach((button) => {
            button.addEventListener('click', () => {
                const { year, month } = button.dataset;
                if (year && month) {
                    const label = container.querySelector('.jctc-header-bar__nav-label');
                    if (label) {
                        label.textContent = formatMonthLabel(year, month);
                    }
                    fetchMonth(year, month);
                }
            });
        });
    }

    bind();
};

export default function initEventCalendars() {
    clearStaleAgendaHash();
    document.querySelectorAll('.jctc-event-calendar').forEach((container) => {
        initCalendarInstance(container);
    });
}
